import { ContentLayout } from '../../Layouts/ContentLayout/ContentLayout';
import { Paginator } from '@/components/Paginator/Paginator';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { BilledEventsTable } from './BilledEventsTable';
import { getBilledEventsList } from './helpers/helpers';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { Button } from '@/components/Button/Button';
import { saveAs } from 'file-saver';
import { useGetSearchParams } from '@/hooks/useGetSearchParams';
import { FilterList } from './ui/FilterList/FilterList';
import {
  BilledEventsFilterParamsType,
  useBilledEventsFilters,
} from './hooks/useBilledEventsFilters/useBilledEventsFilters';
import { getOrganizationOptions } from '@/bundle/_Opco/_BilledEventsPage/ui/FilterList/helpers';
import { getListData, showSnackbarResponseError } from '@/helpers/helpers';
import { loadOrganizations } from '@/bundle/_Opco/api/api';
import { downloadBillingFile, loadBilledEvents } from '@/bundle/_Opco/_BilledEventsPage/api/api';
import { QueryParamsType } from '@/api/helpers/apiParamsHelpers';
import { getFileNameFromHeader } from '@/helpers/fileHelpers';

const ORGANIZATION_LIMIT = 1000;

export const BilledEventsPage = () => {
  const searchParams = useGetSearchParams<BilledEventsFilterParamsType>();

  const { data: organizationsData } = useQuery({
    queryKey: ['load_organizations'],
    queryFn: () => loadOrganizations({ limit: ORGANIZATION_LIMIT }),
    placeholderData: keepPreviousData,
  });

  const organizations = organizationsData?.body?.results;
  const organizationsOptions = getOrganizationOptions(organizations);

  const { queryFilters, filters, applyFilters, clearFilters } = useBilledEventsFilters(organizations);

  const { data: billedEventsData, isPending: isPendingBilledEvents } = useQuery({
    queryKey: ['load_billed_events', searchParams, queryFilters],
    queryFn: () =>
      loadBilledEvents({
        ...searchParams,
        filters: queryFilters,
      }),
    placeholderData: keepPreviousData,
    enabled: !!searchParams.filters,
  });

  const { mutate, isPending } = useMutation({
    mutationKey: ['download_billing_file'],
    mutationFn: () => {
      const payload = {
        ...queryFilters,
      };

      return downloadBillingFile(payload);
    },
    onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }

      const header = response.headers.contentDisposition;
      const fileName = getFileNameFromHeader(header, 'Billing.xlsx');

      saveAs(response?.body, fileName);
    },
  });

  useEffect(() => {
    const billedEventsError = billedEventsData?.error;

    if (!billedEventsError) return;

    const error = getResponseError(billedEventsError) || getResponseError(billedEventsError, 'date_created');

    showSnackbar(error, { variant: 'error', autoClose: false });
  }, [billedEventsData?.error]);

  const { list, totalCount, hasPagination } = getListData(
    billedEventsData?.body,
    searchParams as QueryParamsType & BilledEventsFilterParamsType
  );
  const billedEventsList = getBilledEventsList(list);

  return (
    <ContentLayout
      title='Billing'
      breadcrumbs='Billing'
      subtitle={
        <>
          Please choose the wires that were viewed by an administrator during the selected time period.
          <br />
          By default wires that were viewed in the previous billing period are displayed. This wires will be included in
          the report.
        </>
      }
      headerAction={
        <Button width={140} onClick={mutate} mobileStretch disabled={isPending || !totalCount}>
          {isPending ? 'Exporting...' : 'Export'}
        </Button>
      }
    >
      <PageBody>
        {filters && (
          <FilterList
            filters={filters}
            onApplyFilters={applyFilters}
            onClearFilters={clearFilters}
            organizationsOptions={organizationsOptions}
          />
        )}
        <BilledEventsTable items={billedEventsList} isLoading={isPendingBilledEvents} />
      </PageBody>
      {hasPagination && (
        <PageFooter>
          <Paginator page={searchParams.page} limit={searchParams.limit} total={totalCount} />
        </PageFooter>
      )}
    </ContentLayout>
  );
};
